import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import clsx from 'clsx';
import i18n from 'i18n-js';
import moment from 'moment';
import { forceTimezone } from 'utils';
import Table from '@material-ui/core/Table';
import { withRouter } from 'react-router-dom';
import TableRow from '@material-ui/core/TableRow';
import { Card, CollapseCard } from 'components/UI';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import { Box, List, Typography } from '@material-ui/core';
import TableContainer from '@material-ui/core/TableContainer';
import { EditBusinessHoursRulesetsModal } from 'components/Modals';
import { VehiclesGroupsActions } from 'state/modules/vehiclesGroups';
import { useStyles, collapseCardStyles } from './RulesetsWidget.styled';
import { parseRulesHours, parseBusinessHoursData, defaultBusinessHoursData } from 'services/rules';

const RulesetsTimes = ({ id }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const collapseCardClasses = collapseCardStyles();
  const [openEditModal, setOpenEditModal] = useState(false);
  const { bhTimes } = useSelector((state) => state.vehiclesGroups);
  const { localeFormat } = useSelector((state) => state.organizations);

  const handleOpenEditModal = () => {
    setOpenEditModal((prevState) => !prevState);
  };

  /**
   * Vertical menu list
   * It has the label and the function for each action
   */
  const items = [
    {
      title: i18n.t('common.edit'),
      can: { name: 'vehicle_group', action: 'update' },
      onClick: handleOpenEditModal,
    },
  ];

  useEffect(() => {
    dispatch(VehiclesGroupsActions.fetchVehicleGroupsBHTimes(id));
  }, [dispatch, id]);

  return (
    <Card
      menuItems={items}
      headerActionType="vertical"
      data-cy="bh-times-widget-card"
      title={i18n.t('vehicle_groups.business_hours.title')}
    >
      <Box className={classes.cardContentPadding}>
        {bhTimes?.length > 0 ? (
          <>
            {bhTimes.map((rules, index) => (
              <List className={classes.list} key={index} data-cy="bh-times-widget-card-list">
                <CollapseCard
                  customClasses={collapseCardClasses}
                  title={rules?.business_hours_rules_set?.name}
                >
                  <Box display="flex" component="span" flexWrap="wrap" flexDirection="row">
                    {rules?.start_time && (
                      <Typography variant="body2" component="span" className={classes.time}>
                        <span>{i18n.t('business_hours.effective_from')}</span>{' '}
                        <span className={classes.date}>
                          {forceTimezone(moment(rules?.start_time)).format(localeFormat)}
                        </span>
                      </Typography>
                    )}
                    {rules?.end_time ? (
                      <Typography
                        variant="body2"
                        component="span"
                        color="textSecondary"
                        className={classes.time}
                      >
                        {i18n.t('common.to')}{' '}
                        <span className={classes.date}>
                          {forceTimezone(moment(rules?.end_time)).format(localeFormat)}
                        </span>
                      </Typography>
                    ) : (
                      <Typography
                        variant="body2"
                        component="span"
                        color="textSecondary"
                        className={classes.time}
                      >
                        <span>{i18n.t('business_hours.effective_to')}</span>
                      </Typography>
                    )}
                  </Box>
                  <TableContainer>
                    <Table aria-label="caption table">
                      <TableHead>
                        <TableRow>
                          <TableCell className={clsx(classes.cell, classes.borderNone)}>
                            {i18n.t('business_hours.day')}
                          </TableCell>
                          <TableCell className={clsx(classes.cell, classes.borderNone)}>
                            {`${i18n.t('business_hours.from')} - ${i18n.t('business_hours.to')}`}
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {defaultBusinessHoursData(
                          parseBusinessHoursData(rules?.business_hours_rules_set?.bh_rules)
                        ).map((rule, i) => (
                          <TableRow key={i}>
                            <TableCell className={classes.cell}>
                              <Typography key={i}>
                                <Box fontSize={14} component="span" fontWeight="fontWeightMedium">
                                  {i18n.t(`common.${rule.day}`)}
                                </Box>
                              </Typography>
                            </TableCell>
                            <TableCell className={classes.cell}>
                              {rule?.closed ? (
                                <Typography key={i}>
                                  <Box component="span" fontSize={13}>
                                    {i18n.t('business_hours.closed')}
                                  </Box>
                                </Typography>
                              ) : rule?.fullDay ? (
                                <Typography key={i}>
                                  <Box component="span" fontSize={13}>
                                    {i18n.t('business_hours.open')}
                                  </Box>
                                </Typography>
                              ) : (
                                <>
                                  {rule.hours.map((hours, i) => (
                                    <Typography key={i}>
                                      <Box component="span" fontSize={13}>
                                        {parseRulesHours(hours.start)} -{' '}
                                        {parseRulesHours(hours.end)}
                                      </Box>
                                    </Typography>
                                  ))}
                                </>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CollapseCard>
              </List>
            ))}
          </>
        ) : (
          <Typography color="textPrimary" style={{ lineHeight: 1 }}>
            <Box fontSize={14} component="span" lineHeight={1}>
              {i18n.t('common.empty_list')}
            </Box>
          </Typography>
        )}
      </Box>
      {openEditModal && (
        <EditBusinessHoursRulesetsModal
          open={true}
          currentVehicleGroup={{ id: id }}
          handleClick={handleOpenEditModal}
        />
      )}
    </Card>
  );
};

export default withRouter(RulesetsTimes);
